import { usePersistedState } from "@/lib/persistedFilters";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search, FileText, Eye, Loader2, FileWarning, X, Sparkles, FolderTree, Copy, MessageSquare, Plus, StickyNote, ShieldCheck, Stethoscope, LineChart, BarChart3, Clock, BookOpen, AlertTriangle, Columns3, ImageOff, Lightbulb, ChevronDown, SlidersHorizontal } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { KbIcon } from "@/components/KbIcon";
import { KbNewPill } from "@/components/KbNewPill";
import { KbSnippetPill } from "@/components/KbSnippetPill";
import { KbCategoryFilter } from "@/components/KbCategoryFilter";
import { KbEditDialog } from "@/components/KbEditDialog";
import { KbToolbarButton } from "@/components/KbToolbarButton";
import { KbStyleInjector } from "@/lib/kbSettings";
import { kbHighlight } from "@/lib/kbHighlight";
import { KBLanguageSwitcher } from "@/components/KBLanguageSwitcher";
import { useKbLang, KB_ORIGINAL } from "@/lib/kbLang";
import { Input } from "@/components/ui/input";
import AppUnavailable from "@/pages/AppUnavailable";
import { RequestDialog } from "@/pages/KBPlanner";
import { KbImageHealthCard } from "@/components/KbImageHealthCard";
import { KbReviewInbox } from "@/components/KbReviewInbox";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

/**
 * KB landing — same information architecture as the Helpjuice KB it replaces
 * (hero search over a grid of category cards), rendered in Conduit's own
 * design language rather than Helpjuice's dark chrome.
 */
export default function KnowledgeBase() {
  const navigate = useNavigate();
  const { has } = useAuth();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [denied, setDenied] = useState(false);
  const [q, setQ] = usePersistedState("kb.q", "", params.get("q"));
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [tree, setTree] = useState([]);
  const [catIds, setCatIds] = usePersistedState("kb.cats", [], params.get("cat") ? params.get("cat").split(",").filter(Boolean) : undefined);
  const [createKind, setCreateKind] = useState(null); // "article" | "snippet" | null
  const [aud, setAud] = useState("");
  const [ctype, setCtype] = useState("");
  const [asking, setAsking] = useState(false);
  const [lang] = useKbLang();
  const [strMap, setStrMap] = useState({});

  // Translate the visible content strings (category names/descriptions, article
  // titles, snippets, section headings) so the whole browse experience follows
  // the reader's chosen flag. Cached server-side, so it's instant on repeat.
  useEffect(() => {
    if (lang === KB_ORIGINAL) { setStrMap({}); return undefined; }
    const strings = new Set();
    const push = (s) => { if (s && typeof s === "string" && s.trim()) strings.add(s.trim()); };
    ["Knowledge Base", "Search the Knowledge Base", "Most read", "Recently updated",
      "Help centre", "Something missing? Request an article", "articles across", "categories",
      "— or pick a category to browse it", "Searching…", "for", "result", "results"].forEach(push);
    (data?.categories || []).forEach((c) => { push(c.name); push(c.description); });
    (data?.popular || []).forEach((p) => push(p.name));
    (data?.recent || []).forEach((p) => push(p.name));
    (results?.results || []).forEach((r) => { push(r.name); push(r.snippet); push(r.category_name); });
    const list = [...strings];
    if (!list.length) return undefined;
    let cancelled = false;
    api.post("/kb/translate/strings", { lang, texts: list })
      .then((r) => { if (!cancelled) setStrMap((m) => ({ ...m, ...(r.data?.translations || {}) })); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [lang, data, results]);

  const T = useCallback((s) => {
    if (lang === KB_ORIGINAL || !s || typeof s !== "string") return s;
    return strMap[s.trim()] || s;
  }, [lang, strMap]);

  useEffect(() => {
    api.get("/kb/tree").then((r) => setTree(r.data || [])).catch(() => setTree([]));
    api.get("/kb/landing")
      .then((r) => setData(r.data))
      .catch((e) => { if (e.response?.status === 403) setDenied(true); });
  }, []);

  const runSearch = useCallback(async (term, ids, audience = "", contentType = "") => {
    if (!term || term.trim().length < 2) { setResults(null); return; }
    setSearching(true);
    try {
      const { data: d } = await api.get("/kb/search", {
        params: { q: term, limit: 40,
          ...(ids?.length ? { category_ids: ids.join(",") } : {}),
          ...(audience ? { audience } : {}),
          ...(contentType ? { content_type: contentType } : {}) },
      });
      setResults(d);
    } finally { setSearching(false); }
  }, []);

  const lastParams = useRef(null);
  useEffect(() => {
    const key = params.toString();
    if (lastParams.current === key) return;
    const first = lastParams.current === null;
    lastParams.current = key;
    const term = params.get("q") || "";
    const ids = (params.get("cat") || "").split(",").filter(Boolean);
    if (first && !term && !ids.length) return; // no deep link — keep the remembered search
    setQ(term);
    setCatIds((prev) => (prev.join(",") === ids.join(",") ? prev : ids));
    runSearch(term, ids, aud, ctype);
  }, [params, runSearch]);

  // Search as you type as well, so the box works without pressing anything.
  // Skipped when the URL already carries this term (the effect above ran).
  useEffect(() => {
    if (!q.trim()) {
      // Cleared (X, backspace or "Clear filters") — drop old results and any deep-link ?q/?cat.
      setResults(null);
      if (params.get("q") || params.get("cat")) setParams({}, { replace: true });
      return undefined;
    }
    if (q.trim() === (params.get("q") || "").trim()) return undefined;
    const t = setTimeout(() => runSearch(q.trim(), catIds, aud, ctype), 350);
    return () => clearTimeout(t);
  }, [q, catIds, aud, ctype, params, runSearch, setParams]);

  const submit = (e) => {
    e.preventDefault();
    if (!q.trim()) { setParams({}, { replace: true }); return; }
    setParams({ q: q.trim(), ...(catIds.length ? { cat: catIds.join(",") } : {}) }, { replace: true });
    runSearch(q.trim(), catIds, aud, ctype);
  };

  if (denied) return <AppUnavailable appName="KB" inactive={false} />;
  if (!data) {
    return <div className="flex h-72 items-center justify-center" data-testid="kb-loading">
      <Loader2 className="h-5 w-5 animate-spin text-slate-300 dark:text-slate-600" />
    </div>;
  }

  return (
    <div data-testid="kb-page" className="kb-surface">
      <KbStyleInjector />
      <div className="mb-3 flex items-center justify-end" data-testid="kb-lang-row">
        <KBLanguageSwitcher />
      </div>
      {createKind && (
        <KbEditDialog open kind="article" mode="create" snippet={createKind === "snippet"}
          tree={tree} record={null} onOpenChange={(v) => { if (!v) setCreateKind(null); }}
          onSaved={(a) => { setCreateKind(null); if (a?.slug) navigate(`/kb/a/${a.slug}`); }} />
      )}
      {data.can_edit && (
        <div className="mb-3 flex flex-wrap items-center justify-end gap-2" data-testid="kb-editor-toolbar">
          <KbToolbarButton primary icon={Plus} testid="kb-toolbar-new-article"
            onClick={() => setCreateKind("article")}>New article</KbToolbarButton>
          <KbToolbarButton icon={StickyNote} testid="kb-toolbar-new-snippet"
            onClick={() => setCreateKind("snippet")}>New snippet</KbToolbarButton>
          <KbToolbarButton icon={Sparkles} testid="kb-toolbar-studio"
            onClick={() => navigate("/kb/studio")}>Compose</KbToolbarButton>
          <KbToolbarButton icon={FileText} testid="kb-toolbar-articles"
            onClick={() => navigate("/kb/manage/articles")}>Articles</KbToolbarButton>
          {data.can_manage && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="kb-toolbar-manage-menu"
                  className="flex items-center gap-1.5 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 hover:border-primary/40 hover:text-primary data-[state=open]:border-primary/40 data-[state=open]:text-primary">
                  <SlidersHorizontal className="h-3.5 w-3.5" /> Manage KB
                  <ChevronDown className="h-3.5 w-3.5 opacity-70" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 max-h-[70vh] overflow-y-auto" data-testid="kb-manage-menu-content">
                <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-slate-400">Content tools</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => navigate("/kb/snippets")} data-testid="kb-manage-snippets">
                  <StickyNote className="h-3.5 w-3.5" /> Snippets library
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/kb/planner")} data-testid="kb-manage-planner">
                  <Columns3 className="h-3.5 w-3.5" /> Planner
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/kb/callout-audit")} data-testid="kb-manage-callout-audit">
                  <AlertTriangle className="h-3.5 w-3.5" /> Callout audit
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/kb/cleaned")} data-testid="kb-manage-cleaned">
                  <Sparkles className="h-3.5 w-3.5" /> Recently cleaned
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/kb/image-health")} data-testid="kb-manage-image-health">
                  <ImageOff className="h-3.5 w-3.5" /> Image &amp; link health
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/kb/suggestions")} data-testid="kb-manage-suggestions">
                  <Lightbulb className="h-3.5 w-3.5" /> Suggestions
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-slate-400">Organise &amp; analyse</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => navigate("/kb/visibility")} data-testid="kb-manage-visibility">
                  <ShieldCheck className="h-3.5 w-3.5" /> Visibility
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/kb/category-health")} data-testid="kb-manage-health">
                  <Stethoscope className="h-3.5 w-3.5" /> Health
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/kb/search-insights")} data-testid="kb-manage-insights">
                  <LineChart className="h-3.5 w-3.5" /> Insights
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/kb/usage")} data-testid="kb-manage-usage">
                  <BarChart3 className="h-3.5 w-3.5" /> Usage
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/kb/manage")} data-testid="kb-manage-categories">
                  <FolderTree className="h-3.5 w-3.5" /> Categories
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/kb/duplicates")} data-testid="kb-manage-duplicates">
                  <Copy className="h-3.5 w-3.5" /> Find duplicates
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/kb/feedback")} data-testid="kb-manage-feedback">
                  <MessageSquare className="h-3.5 w-3.5" /> Feedback
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/kb/glossary")} data-testid="kb-manage-glossary">
                  <BookOpen className="h-3.5 w-3.5" /> Glossary
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      )}
      {/* Iter 119b2 — Hero paints itself from the tenant's brand colour
          (--primary is set per partner by BrandingContext), so a white-labelled
          partner sees their own KB rather than Conduit's palette. */}
      <div className="relative mt-2 rounded-2xl px-6 py-10 text-center sm:px-10 sm:py-14"
        style={{ backgroundColor: "hsl(var(--primary))" }}>
        <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-2xl">
          <div className="absolute inset-0 bg-slate-950/45" />
          <div className="absolute inset-0 opacity-25"
            style={{ backgroundImage: "radial-gradient(circle at 15% 15%, rgba(255,255,255,0.55) 0, transparent 42%), radial-gradient(circle at 88% 85%, rgba(0,0,0,0.35) 0, transparent 45%)" }} />
        </div>
        <div className="relative">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/20 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-white"
            data-testid="kb-beta-pill">
            <Sparkles className="h-3 w-3" /> {data.can_edit ? "Editor mode" : "Help centre"}
          </span>
          <h1 className="mt-4 font-heading text-4xl font-bold text-white sm:text-5xl">{T("Knowledge Base")}</h1>
          <form onSubmit={submit} className="mx-auto mt-6 flex max-w-2xl flex-col gap-2 sm:flex-row">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
              <Input data-testid="kb-search-input" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder={T("Search the Knowledge Base")}
                className="h-12 rounded-full border-0 bg-white dark:bg-slate-900 pl-11 pr-11 text-base shadow-lg focus-visible:ring-2" />
              {q && (
                <button type="button" data-testid="kb-search-clear"
                  onClick={() => { setQ(""); setCatIds([]); setParams({}, { replace: true }); }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 hover:text-slate-600">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            {/* Category picker — with a query it narrows the search to those
                trees; picking exactly one on its own takes you straight to it. */}
            <KbCategoryFilter tree={tree} value={catIds} variant="pill" testid="kb-search-category"
              onChange={(ids) => {
                setCatIds(ids);
                if (q.trim()) {
                  setParams({ q: q.trim(), ...(ids.length ? { cat: ids.join(",") } : {}) },
                    { replace: true });
                  return;
                }
                if (ids.length !== 1) return;
                const hit = tree.find((c) => c.cat_id === ids[0]);
                if (hit?.slug) navigate(`/kb/c/${hit.slug}`);
              }} />
            {/* A real submit button: a form with two fields and no submit button
                never fires implicit submission, so Enter used to do nothing. */}
            <button type="submit" data-testid="kb-search-submit"
              className="h-12 rounded-full bg-white/15 px-6 text-sm font-semibold text-white ring-1 ring-inset ring-white/30 transition-colors hover:bg-white/25">
              Search
            </button>
          </form>
          <p className="mt-3 text-sm text-white/70">
            {data.stats.published.toLocaleString()} {T("articles across")} {data.stats.categories} {T("categories")}
            <span className="ml-1 text-white/50">{T("— or pick a category to browse it")}</span>
          </p>
          {/* Open to everyone: gaps get captured instead of forgotten. */}
          <button type="button" onClick={() => setAsking(true)} data-testid="kb-request-article-btn"
            className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-white/15 px-4 py-2 text-xs font-semibold text-white ring-1 ring-inset ring-white/30 transition-colors hover:bg-white/25">
            <Plus className="h-3.5 w-3.5" /> Something missing? Request an article
          </button>
        </div>
      </div>

      {asking && <RequestDialog onClose={() => setAsking(false)} onSaved={() => setAsking(false)} />}

      {results ? (
        <section className="mt-8" data-testid="kb-search-results">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-heading text-lg font-semibold text-slate-800 dark:text-slate-100">
              {searching ? T("Searching…") : `${results.count} ${T(results.count === 1 ? "result" : "results")} ${T("for")} “${results.query}”`}
              {results.fuzzy && <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">did you mean…</span>}
            </h2>
            <div className="flex items-center gap-2" data-testid="kb-search-filters">
              <select data-testid="kb-search-filter-type" value={ctype}
                onChange={(e) => { setCtype(e.target.value); runSearch(q, catIds, aud, e.target.value); }}
                className="rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-2 py-1 text-xs text-slate-600 dark:text-slate-300">
                <option value="">All types</option>
                <option value="article">Articles</option>
                <option value="snippet">Snippets</option>
              </select>
              {data.can_edit && (
                <select data-testid="kb-search-filter-audience" value={aud}
                  onChange={(e) => { setAud(e.target.value); runSearch(q, catIds, e.target.value, ctype); }}
                  className="rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-2 py-1 text-xs text-slate-600 dark:text-slate-300">
                  <option value="">All audiences</option>
                  <option value="everyone">Everyone</option>
                  <option value="agent">Agents</option>
                  <option value="reseller">Resellers</option>
                  <option value="master_agent">Master Agents</option>
                  <option value="service_provider">Service Providers</option>
                  <option value="referral">Referral</option>
                  <option value="internal">Internal</option>
                </select>
              )}
            </div>
          </div>
          {!searching && results.count === 0 && (
            <div className="rounded-lg border border-dashed border-slate-200 dark:border-slate-800 py-14 text-center text-sm text-slate-400 dark:text-slate-500"
              data-testid="kb-search-empty">
              Nothing matched. Try a shorter phrase or a device model number.
            </div>
          )}
          <div className="space-y-2">
            {results.results.map((r) => (
              <button key={r.article_id} data-testid={`kb-result-${r.slug}`}
                onClick={() => {
                  // Which search led where — feeds the keyword suggestions.
                  api.post("/kb/search/click", { query: results.query || q, slug: r.slug })
                    .catch(() => {});
                  navigate(`/kb/a/${r.slug}`);
                }}
                className="block w-full rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 text-left transition-colors hover:border-primary/40 hover:bg-slate-50 dark:hover:bg-slate-800">
                <div className="flex items-start gap-3">
                  <FileText className="mt-0.5 h-4 w-4 shrink-0 text-slate-300 dark:text-slate-600" />
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-slate-800 dark:text-slate-100">{kbHighlight(T(r.name), results.query)}</span>
                      {r.is_new && <KbNewPill />}
                      {r.is_snippet && <KbSnippetPill />}
                      {r.category_name && <span className="rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-[10px] text-slate-500 dark:text-slate-400">{T(r.category_name)}</span>}
                      {r.is_draft && <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">Draft</span>}
                    </div>
                    {r.snippet && <p className="mt-1 line-clamp-2 text-xs text-slate-500 dark:text-slate-400">{kbHighlight(T(r.snippet), results.query)}</p>}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </section>
      ) : (
        <>
          {/* KB management tools only (kb_admin.view — the same gate as the
              link-health endpoints): the weekly image & link sweep under the hero. */}
          {data.can_edit && <KbReviewInbox />}
          {data.can_manage && has("kb_admin.view") && <KbImageHealthCard />}
          <section className="mt-8" data-testid="kb-category-grid">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {data.categories.map((c) => (
                <button key={c.cat_id} data-testid={`kb-category-card-${c.slug}`}
                  onClick={() => navigate(`/kb/c/${c.slug}`)}
                  className="group flex h-full flex-col items-center rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-5 py-7 text-center shadow-sm transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 transition-colors group-hover:bg-primary/10 group-hover:text-primary">
                    <KbIcon icon={c.icon} name={c.name} />
                  </span>
                  <span className="mt-3 flex items-center gap-2 font-heading text-base font-semibold text-slate-800 dark:text-slate-100">{T(c.name.trim())}{c.is_new && <KbNewPill />}</span>
                  {c.description && <span className="mt-1.5 line-clamp-3 text-xs text-slate-500 dark:text-slate-400">{T(c.description)}</span>}
                  <span className="mt-3 text-[11px] font-medium text-slate-400 dark:text-slate-500">
                    {c.article_count_deep} article{c.article_count_deep === 1 ? "" : "s"}
                  </span>
                </button>
              ))}
            </div>
          </section>

          <section className="mt-10" data-testid="kb-popular">
            <h2 className="mb-3 font-heading text-lg font-semibold text-slate-800 dark:text-slate-100">{T("Most read")}</h2>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
              {data.popular.map((p) => (
                <button key={p.article_id} data-testid={`kb-popular-${p.slug}`}
                  onClick={() => navigate(`/kb/a/${p.slug}`)}
                  className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-4 py-3 text-left transition-colors hover:border-primary/40 hover:bg-slate-50 dark:hover:bg-slate-800">
                  <span className="flex min-w-0 items-center gap-2 truncate text-sm font-medium text-slate-700 dark:text-slate-200">{T(p.name)}{p.is_new && <KbNewPill />}</span>
                  <span className="flex shrink-0 items-center gap-1 text-xs text-slate-400 dark:text-slate-500">
                    <Eye className="h-3.5 w-3.5" /> {p.hj_views.toLocaleString()}
                  </span>
                </button>
              ))}
            </div>
          </section>

          {data.recent?.length > 0 && (
            <section className="mt-10" data-testid="kb-recent">
              <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-semibold text-slate-800 dark:text-slate-100">
                <Clock className="h-4 w-4 text-slate-400 dark:text-slate-500" /> {T("Recently updated")}
              </h2>
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                {data.recent.map((p) => (
                  <button key={p.article_id} data-testid={`kb-recent-${p.slug}`}
                    onClick={() => navigate(`/kb/a/${p.slug}`)}
                    className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-4 py-3 text-left transition-colors hover:border-primary/40 hover:bg-slate-50 dark:hover:bg-slate-800">
                    <span className="flex min-w-0 items-center gap-2 truncate text-sm font-medium text-slate-700 dark:text-slate-200">{T(p.name)}{p.is_new && <KbNewPill />}</span>
                    <span className="shrink-0 text-xs text-slate-400 dark:text-slate-500">{(p.source_updated_at || "").slice(0, 10)}</span>
                  </button>
                ))}
              </div>
            </section>
          )}

          {data.stats.missing_assets > 0 && (
            <button data-testid="kb-missing-assets-link" onClick={() => navigate("/kb/missing-assets")}
              className="mt-8 flex w-full items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-left transition-colors hover:bg-amber-100">
              <FileWarning className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <span className="text-sm text-amber-900">
                <span className="font-semibold">{data.stats.missing_assets} attachments need re-uploading.</span>{" "}
                Helpjuice hosted these on private S3 storage that returns 403, so they couldn't be imported. Review the list →
              </span>
            </button>
          )}
        </>
      )}
    </div>
  );
}
